/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import {FC, lazy, Suspense} from 'react';
import {Routes, Route, BrowserRouter, Navigate} from 'react-router-dom';
import {PrivateRoutes} from './PrivateRoutes';
import {ErrorsPage} from '../modules/errors/ErrorsPage';
import {Logout, AuthPage, useAuth} from '../modules/auth';
import {App} from '../App';
import useRouteRedirection from 'app/shared/hooks/useRouteRedirection';
import {OPS_OVERVIEW_DEV_PREVIEW} from 'app/modules/operationalOverview/overviewFlags';

// Preview sem login da Visão operacional (só no servidor de dev; fixtures).
const OperationalOverviewPreview = lazy(
  () => import('../modules/operationalOverview/views/OperationalOverviewPage')
);

/**
 * Base URL of the website.
 *
 * @see https://facebook.github.io/create-react-app/docs/using-the-public-folder
 */
const {PUBLIC_URL} = process.env;

const AppRoutes: FC = () => {
  const {currentUser} = useAuth();
  const {routeRedirectionByUser} = useRouteRedirection();

  return (
    <BrowserRouter basename={PUBLIC_URL}>
      <Routes>
        <Route element={<App />}>
          <Route path='error/*' element={<ErrorsPage />} />
          <Route path='logout' element={<Logout />} />
          {OPS_OVERVIEW_DEV_PREVIEW && (
            <Route
              path='dev/visao-operacional'
              element={
                <Suspense fallback={null}>
                  <OperationalOverviewPreview embedded={false} />
                </Suspense>
              }
            />
          )}
          {currentUser ? (
            <>
              <Route path='/*' element={<PrivateRoutes />} />
              <Route
                index
                element={
                  <Navigate to={routeRedirectionByUser('/quotes/workspace')} />
                }
              />
            </>
          ) : (
            <>
              <Route path='auth/*' element={<AuthPage />} />
              <Route path='*' element={<Navigate to='/auth' />} />
            </>
          )}
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

export {AppRoutes};
